/**
 * ThemeEffects - Visual effects for theme transitions
 * Copyright 2024 Delanoe Pirard / Aedelon - Apache 2.0
 */

export class ThemeEffects {
  constructor(themeState) {
    this.themeState = themeState;
    this.mousePosition = { x: 0, y: 0 };
    this.isTransitioning = false;

    // Mouse tracking disabled for performance
    // (was used for particle effects)
  }

  /**
   * Creates a ripple effect from click position
   */
  createRippleEffect(e) {
    const x = e.clientX;
    const y = e.clientY;

    const ripple = document.createElement('div');
    ripple.className = 'theme-transition-ripple';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    ripple.style.width = '0px';
    ripple.style.height = '0px';

    document.body.appendChild(ripple);

    requestAnimationFrame(() => {
      ripple.style.transition = 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)';
      ripple.style.width = '2000px';
      ripple.style.height = '2000px';
      ripple.style.opacity = '0';
    });

    setTimeout(() => ripple.remove(), 1500);
  }

  /**
   * Magnetic button effect on hover
   */
  magneticEffect(e, toggleElement) {
    if (!toggleElement || this.isTransitioning) return;

    const rect = toggleElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;

    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const maxDistance = 50;

    if (distance < maxDistance) {
      const force = (maxDistance - distance) / maxDistance;
      const translateX = deltaX * force * 0.2;
      const translateY = deltaY * force * 0.2;

      toggleElement.style.transform = `translate(${translateX}px, ${translateY}px) scale(${1 + force * 0.05})`;
    }
  }

  /**
   * Reset magnetic effect
   */
  resetMagneticEffect(toggleElement) {
    if (toggleElement) {
      toggleElement.style.transform = '';
    }
  }

  /**
   * Particle burst effect on theme change
   */
  createParticleEffect(isDark) {
    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'theme-particle';
      particle.style.cssText = `
        position: fixed;
        width: ${Math.random() * 4 + 2}px;
        height: ${Math.random() * 4 + 2}px;
        background: ${isDark ? '#818CF8' : '#FFD700'};
        border-radius: 50%;
        pointer-events: none;
        z-index: 10000;
        left: ${this.mousePosition.x}px;
        top: ${this.mousePosition.y}px;
        opacity: 1;
      `;

      document.body.appendChild(particle);

      const angle = (Math.PI * 2 * i) / particleCount;
      const velocity = Math.random() * 100 + 50;
      const duration = Math.random() * 800 + 400;

      particle.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${Math.cos(angle) * velocity}px, ${Math.sin(angle) * velocity}px) scale(0)`, opacity: 0 }
      ], {
        duration: duration,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
      });

      setTimeout(() => particle.remove(), duration);
    }
  }

  /**
   * Flash effect on theme change
   */
  createFlashEffect(isDark) {
    const flash = document.createElement('div');
    flash.style.cssText = `
      position: fixed;
      inset: 0;
      background: ${isDark ? 'rgba(17, 24, 39, 0.3)' : 'rgba(255, 255, 255, 0.3)'};
      pointer-events: none;
      z-index: 9998;
      opacity: 0;
      transition: opacity 0.2s ease;
    `;

    document.body.appendChild(flash);

    requestAnimationFrame(() => {
      flash.style.opacity = '1';
      setTimeout(() => {
        flash.style.opacity = '0';
        setTimeout(() => flash.remove(), 200);
      }, 50);
    });
  }

  /**
   * Enhanced transition with overlay
   */
  performTransition(theme, onMidTransition) {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    const isDark = theme === 'dark';

    const overlay = document.createElement('div');
    overlay.className = 'theme-transition-overlay';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: ${isDark ? 'rgba(17, 24, 39, 0.5)' : 'rgba(255, 255, 255, 0.5)'};
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });

    // Execute callback midway through transition
    setTimeout(() => {
      if (onMidTransition) onMidTransition();
      this.createParticleEffect(isDark);
    }, 150);

    // Remove overlay
    setTimeout(() => {
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        this.isTransitioning = false;
      }, 300);
    }, 300);
  }

  /**
   * Show notification toast
   */
  showNotification(message) {
    const existing = document.querySelector('.theme-mode-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.className = 'theme-mode-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--bg-primary);
      color: var(--text-primary);
      padding: 12px 20px;
      border-radius: 25px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: var(--shadow-lg);
      border: 1px solid var(--border-color);
      z-index: 10000;
      opacity: 0;
      transform: translateX(100px);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      pointer-events: none;
    `;

    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    });

    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100px)';
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }
}
